/**
 * Frequently Bought Together (FBT) Bundle Recommendations
 * Uses Shopify's native Product Recommendations API with intent=complementary
 * Displays smart bundles in a center modal after add-to-cart events
 * 
 * UX Strategy: Center modal after cart add (non-intrusive, high visibility)
 * - Triggers automatically after successful add-to-cart
 * - Shows 2-4 complementary products based on Shopify's AI
 * - Offers bundle discount for increased AOV
 * - Easy to dismiss (ESC key, overlay click, close button)
 */

class CartFBT {
  constructor() {
    // Wait for settings to be available
    this.settings = window.labratgifts_fbt_settings || { enabled: false };
    
    if (!this.settings.enabled) {
      console.log('FBT feature is disabled in theme settings');
      return;
    }

    this.modal = document.getElementById('fbtModal');
    
    if (!this.modal) {
      console.log('FBT modal element not found in DOM');
      return;
    }
    
    this.recommendationsContainer = document.getElementById('fbtRecommendations');
    this.bundlePriceElement = document.getElementById('fbtBundlePrice');
    this.originalPriceElement = document.getElementById('fbtOriginalPrice');
    this.addBundleBtn = document.getElementById('fbtAddBundleBtn');
    
    this.selectedProducts = [];
    this.currentProductId = null;
    this.recommendations = [];
    
    this.init();
    
    // Mark as successfully initialized
    window.labratgifts_fbt_initialized = true;
  }

  init() {
    console.log('Initializing FBT modal', this.settings);
    
    // Listen for add-to-cart events
    this.setupCartListeners();
    
    // Setup modal controls
    this.setupModalControls();
    
    // Setup product selection
    this.setupBundleControls();
  }

  setupCartListeners() {
    console.log('FBT: Setting up cart listeners (native events only)');
    
    // Listen for custom cart:item-added events
    document.addEventListener('cart:item-added', (event) => {
      console.log('FBT: cart:item-added event received', event.detail);
      if (event.detail && event.detail.product_id) {
        // Capture request ID for coordinated response
        const requestId = event.detail.requestId;
        setTimeout(() => {
          this.handleProductAdded(event.detail.product_id, requestId);
        }, this.settings.autoShowDelay);
      }
    });

    // Listen for all add-to-cart form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target;
      
      // Check if this is a product form (add to cart)
      if (form.matches('[action*="/cart/add"]') || form.querySelector('[name="id"]')) {
        console.log('FBT: Add to cart form submitted');
        
        // Extract product ID from the form
        const variantIdInput = form.querySelector('[name="id"]');
        if (variantIdInput) {
          const variantId = variantIdInput.value;
          
          // Get the product ID from the form's data attribute or page context
          let productId = form.dataset.productId || form.closest('[data-product-id]')?.dataset.productId;
          
          // If we have a product ID, trigger FBT after delay
          if (productId) {
            console.log('FBT: Product ID found from form:', productId);
            setTimeout(() => {
              this.handleProductAdded(parseInt(productId));
            }, this.settings.autoShowDelay + 500); // Extra delay to ensure cart updates
          } else {
            // Fallback: try to get product ID from the page
            const productForm = document.querySelector('form[action*="/cart/add"]');
            if (productForm) {
              productId = productForm.dataset.productId;
              if (productId) {
                console.log('FBT: Product ID found from page form:', productId);
                setTimeout(() => {
                  this.handleProductAdded(parseInt(productId));
                }, this.settings.autoShowDelay + 500);
              }
            }
          }
        }
      }
    });

    // Also listen for Shopify's theme:cart:change event (if available)
    document.addEventListener('theme:cart:change', (event) => {
      console.log('FBT: theme:cart:change event received', event.detail);
      if (event.detail && event.detail.product_id) {
        setTimeout(() => {
          this.handleProductAdded(event.detail.product_id);
        }, this.settings.autoShowDelay);
      }
    });
  }

  async handleProductAdded(productId, requestId) {
    console.log('Product added to cart:', productId, 'requestId:', requestId);
    this.currentProductId = productId;
    
    // Fetch recommendations, passing requestId through the chain
    await this.loadRecommendations(productId, requestId);
  }

  // Fetch product with collections for campaign pricing
  async fetchProductWithCollections(productHandle) {
    try {
      const response = await fetch(`${window.Shopify.routes.root}products/${productHandle}.json`);
      if (response.ok) {
        const data = await response.json();
        return data.product;
      }
    } catch (e) {
      console.warn('Failed to fetch product details:', productHandle, e);
    }
    return null;
  }

  async loadRecommendations(productId, requestId) {
    this.showLoading();
    
    // Dispatch event to let other components know FBT is checking for recommendations
    document.dispatchEvent(new CustomEvent('fbt:checking'));

    try {
      // Get source product details to determine its category
      const sourceProductResponse = await fetch(
        `${window.Shopify.routes.root}products/${productId}.json`
      );
      let sourceProduct = null;
      let sourceCategory = null;
      
      if (sourceProductResponse.ok) {
        const sourceData = await sourceProductResponse.json();
        sourceProduct = sourceData.product;
        // Get category from product type or tags
        sourceCategory = sourceProduct.product_type || sourceProduct.tags?.[0] || null;
        console.log('FBT: Source product category:', sourceCategory);
      }

      // Use Shopify's Product Recommendations API with complementary intent
      const response = await fetch(
        `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${Math.max(8, this.settings.recommendationsCount * 3)}&intent=complementary`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      let allRecommendations = data.products || [];

      // Filter to show cross-category products (exclude same category)
      if (sourceCategory && allRecommendations.length > 0) {
        const crossCategoryProducts = allRecommendations.filter(product => {
          const productCategory = product.product_type || product.tags?.[0] || null;
          // Keep products from different categories
          return productCategory !== sourceCategory;
        });
        
        // If we have enough cross-category products, use them
        if (crossCategoryProducts.length >= this.settings.recommendationsCount) {
          this.recommendations = crossCategoryProducts.slice(0, this.settings.recommendationsCount);
          console.log('FBT: Using cross-category products:', this.recommendations.map(p => p.product_type));
        } else if (crossCategoryProducts.length > 0) {
          // Mix cross-category with same category if not enough
          this.recommendations = [
            ...crossCategoryProducts.slice(0, Math.ceil(this.settings.recommendationsCount * 0.7)),
            ...allRecommendations.filter(p => (p.product_type || p.tags?.[0]) === sourceCategory).slice(0, Math.floor(this.settings.recommendationsCount * 0.3))
          ].slice(0, this.settings.recommendationsCount);
          console.log('FBT: Mixed cross-category and same-category products');
        } else {
          // No cross-category found, use all recommendations
          this.recommendations = allRecommendations.slice(0, this.settings.recommendationsCount);
        }
      } else {
        this.recommendations = allRecommendations.slice(0, this.settings.recommendationsCount);
      }

      // Fallback to related products if not enough complementary
      if (this.recommendations.length < this.settings.recommendationsCount) {
        const fallbackResponse = await fetch(
          `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${Math.max(8, this.settings.recommendationsCount * 3)}&intent=related`
        );
        
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const fallbackProducts = fallbackData.products || [];
          
          // Filter fallback for cross-category as well
          if (sourceCategory) {
            const crossCategoryFallback = fallbackProducts.filter(product => {
              const productCategory = product.product_type || product.tags?.[0] || null;
              return productCategory !== sourceCategory && !this.recommendations.find(r => r.id === product.id);
            });
            this.recommendations = [
              ...this.recommendations,
              ...crossCategoryFallback
            ].slice(0, this.settings.recommendationsCount);
          } else {
            this.recommendations = [
              ...this.recommendations,
              ...fallbackProducts.filter(p => !this.recommendations.find(r => r.id === p.id))
            ].slice(0, this.settings.recommendationsCount);
          }
        }
      }

      if (this.recommendations.length > 0) {
        this.showModal();
        this.renderRecommendations();
        // Only dispatch event if we have a valid request ID (from quick view)
        if (requestId) {
          document.dispatchEvent(new CustomEvent('fbt:will-show', {
            detail: { requestId: requestId }
          }));
        }
      } else {
        // No recommendations, FBT won't show
        this.hideModal();
        // Only dispatch event if we have a valid request ID (from quick view)
        if (requestId) {
          document.dispatchEvent(new CustomEvent('fbt:will-not-show', {
            detail: { requestId: requestId }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading FBT recommendations:', error);
      this.hideModal();
      // Only dispatch event if we have a valid request ID (from quick view)
      if (requestId) {
        document.dispatchEvent(new CustomEvent('fbt:will-not-show', {
          detail: { requestId: requestId }
        }));
      }
    }
  }

  // Get campaign discount for a product
  getCampaignDiscount(product) {
    const campaign = window.campaignSettings || { enabled: false };
    if (!campaign.enabled) return { useCampaign: false, discount: 0 };
    
    // Get collection handles from product
    const productCollections = product.collections || [];
    const productCollHandles = productCollections.map(c => typeof c === 'string' ? c : c.handle);
    
    // Use collectionPriority array to match Liquid's priority order
    if (campaign.collectionPriority && productCollHandles.length > 0) {
      for (const campaignColl of campaign.collectionPriority) {
        if (campaignColl && campaignColl.handle && productCollHandles.includes(campaignColl.handle)) {
          return { useCampaign: true, discount: campaignColl.discount };
        }
      }
    }
    
    // Fallback to collections object
    if (campaign.collections && productCollHandles.length > 0) {
      for (const handle of productCollHandles) {
        if (campaign.collections[handle] !== undefined) {
          return { useCampaign: true, discount: campaign.collections[handle] };
        }
      }
    }
    
    // Apply default if apply to all is enabled
    if (campaign.applyToAll) {
      return { useCampaign: true, discount: campaign.defaultDiscount };
    }
    
    return { useCampaign: false, discount: 0 };
  }

  renderRecommendations() {
    this.hideLoading();
    
    const campaign = window.campaignSettings || { enabled: false, name: 'SALE' };
    
    const html = this.recommendations.map((product, index) => {
      const variant = product.variants[0];
      const imageUrl = product.featured_image || product.images[0]?.src || '';
      const originalPrice = variant.price;
      const comparePrice = variant.compare_at_price > variant.price ? variant.compare_at_price : null;
      
      // Calculate campaign pricing
      const campaignInfo = this.getCampaignDiscount(product);
      let displayPrice = originalPrice;
      let priceHTML = '';
      
      if (campaignInfo.useCampaign && campaignInfo.discount > 0) {
        // Calculate campaign price
        const campaignPrice = Math.round(originalPrice * (100 - campaignInfo.discount) / 100);
        displayPrice = campaignPrice;
        
        // Campaign pricing display: Badge + WAS/REG → NOW + SAVE (matching PDP pattern)
        priceHTML = `
          <div class="labratgifts-fbt-product__campaign-badge">
            <span class="labratgifts-badge labratgifts-badge--campaign labratgifts-badge--small">${campaign.name}</span>
          </div>
          <div class="labratgifts-fbt-product__price-row labratgifts-fbt-product__price-row--old">
            <span class="labratgifts-fbt-product__price-label">WAS</span>
            <span class="labratgifts-fbt-product__price--compare">${this.formatMoney(originalPrice)}</span>
            ${comparePrice ? `
              <span class="labratgifts-fbt-product__price-label">REG</span>
              <span class="labratgifts-fbt-product__price--compare">${this.formatMoney(comparePrice)}</span>
            ` : ''}
          </div>
          <div class="labratgifts-fbt-product__price-row labratgifts-fbt-product__price-row--now">
            <span class="labratgifts-fbt-product__price-label labratgifts-fbt-product__price-label--now">NOW</span>
            <span class="labratgifts-fbt-product__price--current">${this.formatMoney(campaignPrice)}</span>
            <span class="labratgifts-fbt-product__save-badge">SAVE ${campaignInfo.discount}%</span>
          </div>
        `;
      } else if (comparePrice) {
        // Regular sale pricing (matching PDP pattern without campaign badge)
        const savingsPercent = Math.round(((comparePrice - originalPrice) / comparePrice) * 100);
        priceHTML = `
          <div class="labratgifts-fbt-product__price-row labratgifts-fbt-product__price-row--old">
            <span class="labratgifts-fbt-product__price-label">WAS</span>
            <span class="labratgifts-fbt-product__price--compare">${this.formatMoney(comparePrice)}</span>
          </div>
          <div class="labratgifts-fbt-product__price-row labratgifts-fbt-product__price-row--now">
            <span class="labratgifts-fbt-product__price-label labratgifts-fbt-product__price-label--now">NOW</span>
            <span class="labratgifts-fbt-product__price--current">${this.formatMoney(originalPrice)}</span>
            <span class="labratgifts-fbt-product__save-badge">SAVE ${savingsPercent}%</span>
          </div>
        `;
      } else {
        // Regular price
        priceHTML = `<span class="labratgifts-fbt-product__price--current">${this.formatMoney(originalPrice)}</span>`;
      }
      
      return `
        <div class="labratgifts-fbt-product" data-product-id="${product.id}" data-variant-id="${variant.id}" data-price="${displayPrice}" data-original-price="${originalPrice}">
          <label class="labratgifts-fbt-product__checkbox-wrapper">
            <input type="checkbox" 
                   class="labratgifts-fbt-product__checkbox" 
                   data-fbt-select
                   checked
                   data-product-index="${index}">
            <div class="labratgifts-fbt-product__image">
              <img src="${imageUrl}" alt="${product.title}" loading="lazy">
            </div>
          </label>
          
          <div class="labratgifts-fbt-product__info">
            <h3 class="labratgifts-fbt-product__title">${product.title}</h3>
            <div class="labratgifts-fbt-product__price">
              ${priceHTML}
            </div>
            ${product.available ? '' : '<span class="labratgifts-fbt-product__sold-out">Sold Out</span>'}
          </div>
        </div>
      `;
    }).join('');

    this.recommendationsContainer.innerHTML = html;
    this.recommendationsContainer.style.display = 'grid';
    
    // Initialize all as selected with campaign-adjusted prices
    this.selectedProducts = this.recommendations.map((p, i) => {
      const campaignInfo = this.getCampaignDiscount(p);
      const originalPrice = p.variants[0].price;
      const displayPrice = campaignInfo.useCampaign && campaignInfo.discount > 0
        ? Math.round(originalPrice * (100 - campaignInfo.discount) / 100)
        : originalPrice;
      
      return {
        id: p.id,
        variantId: p.variants[0].id,
        price: displayPrice,
        originalPrice: originalPrice,
        title: p.title,
        available: p.available
      };
    });
    
    this.updateBundlePrice();
  }

  setupModalControls() {
    // Close modal handlers
    const closeBtns = this.modal.querySelectorAll('[data-fbt-close]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  setupBundleControls() {
    // Product selection checkboxes
    if (this.recommendationsContainer) {
      this.recommendationsContainer.addEventListener('change', (e) => {
        if (e.target.matches('[data-fbt-select]')) {
          const checkbox = e.target;
          const productCard = checkbox.closest('.labratgifts-fbt-product');
          const productId = parseInt(productCard.dataset.productId);
          const variantId = parseInt(productCard.dataset.variantId);
          const price = parseInt(productCard.dataset.price); // Campaign-adjusted price
          const originalPrice = parseInt(productCard.dataset.originalPrice) || price;
          const index = parseInt(checkbox.dataset.productIndex);
          
          if (checkbox.checked) {
            // Add product to bundle
            const product = this.recommendations[index];
            this.selectedProducts.push({
              id: productId,
              variantId: variantId,
              price: price,
              originalPrice: originalPrice,
              title: product.title,
              available: product.available
            });
          } else {
            // Remove product from bundle
            this.selectedProducts = this.selectedProducts.filter(p => p.id !== productId);
          }
          
          this.updateBundlePrice();
        }
      });
    }

    // Add bundle to cart
    if (this.addBundleBtn) {
      this.addBundleBtn.addEventListener('click', async () => {
        await this.addBundleToCart();
      });
    }
  }

  updateBundlePrice() {
    const availableProducts = this.selectedProducts.filter(p => p.available);
    // Use campaign-adjusted prices for bundle total
    const totalCampaignPrice = availableProducts.reduce((sum, p) => sum + p.price, 0);
    const totalOriginalPrice = availableProducts.reduce((sum, p) => sum + (p.originalPrice || p.price), 0);
    
    // Apply bundle discount on top of campaign prices
    const discountMultiplier = 1 - (this.settings.bundleDiscount / 100);
    const finalBundlePrice = Math.round(totalCampaignPrice * discountMultiplier);
    
    // Calculate total savings (from original to final bundle price)
    const totalSavings = totalOriginalPrice - finalBundlePrice;
    const savingsPercent = totalOriginalPrice > 0 ? Math.round((totalSavings / totalOriginalPrice) * 100) : 0;

    if (this.bundlePriceElement) {
      this.bundlePriceElement.textContent = this.formatMoney(finalBundlePrice);
    }

    if (this.originalPriceElement) {
      // Show original price if there's any discount (campaign or bundle)
      if (totalOriginalPrice > finalBundlePrice) {
        this.originalPriceElement.textContent = this.formatMoney(totalOriginalPrice);
        this.originalPriceElement.style.display = 'inline';
      } else {
        this.originalPriceElement.style.display = 'none';
      }
    }
    
    // Update savings badge if exists
    const savingsBadge = this.modal.querySelector('.labratgifts-fbt-savings-badge');
    if (savingsBadge && savingsPercent > 0) {
      savingsBadge.textContent = `SAVE ${savingsPercent}%`;
      savingsBadge.style.display = 'inline';
    } else if (savingsBadge) {
      savingsBadge.style.display = 'none';
    }

    // Show/hide bundle summary
    const bundleSummary = this.modal.querySelector('.labratgifts-fbt-bundle-summary');
    if (bundleSummary) {
      bundleSummary.style.display = availableProducts.length > 0 ? 'block' : 'none';
    }

    // Enable/disable add button
    if (this.addBundleBtn) {
      this.addBundleBtn.disabled = availableProducts.length === 0;
    }
  }

  async addBundleToCart() {
    const availableProducts = this.selectedProducts.filter(p => p.available);
    
    if (availableProducts.length === 0) return;

    this.addBundleBtn.disabled = true;
    this.addBundleBtn.innerHTML = `
      <span class="labratgifts-btn-spinner"></span>
      Adding to cart...
    `;

    try {
      // Add all products to cart
      const requests = availableProducts.map(product => 
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: product.variantId,
            quantity: 1
          })
        })
      );

      await Promise.all(requests);

      // Update cart count and refresh drawer
      const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartResponse.json();
      
      // Use global updateCartCount if available
      if (window.updateCartCount) {
        window.updateCartCount(cart.item_count);
      } else {
        // Fallback: update cart count in UI
        const cartCounts = document.querySelectorAll('.labratgifts-cart-count span, [data-cart-count]');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.classList.remove('labratgifts-cart-count--hidden');
        });
      }
      
      // Dispatch event for cart update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      
      // Refresh cart drawer content before opening
      if (window.refreshCartDrawer) {
        await window.refreshCartDrawer(cart);
      }
      
      // Show toast notification
      if (window.showToast) {
        window.showToast(`${availableProducts.length} items added to cart!`, 'success');
      }

      // Close FBT modal and open cart drawer
      this.closeModal();
      
      // Open cart drawer
      const cartDrawer = document.getElementById('cartDrawer');
      if (cartDrawer) {
        cartDrawer.classList.add('active');
        document.body.style.overflow = 'hidden';
      }

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      alert('Sorry, there was an error adding products to your cart. Please try again.');
    } finally {
      this.addBundleBtn.disabled = false;
      this.addBundleBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 1h3l1 9h8"/>
          <circle cx="6" cy="13" r="1" fill="currentColor"/>
          <circle cx="12" cy="13" r="1" fill="currentColor"/>
          <path d="M5 4h10l-1 5H5"/>
        </svg>
        ${this.settings.ctaText || 'Add Bundle to Cart'}
      `;
    }
  }

  showModal() {
    if (this.modal) {
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  closeModal() {
    if (this.modal) {
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Reset state
      setTimeout(() => {
        this.selectedProducts = [];
        this.recommendations = [];
        if (this.recommendationsContainer) {
          this.recommendationsContainer.innerHTML = '';
        }
      }, 300);
    }
  }

  showLoading() {
    const loading = this.modal.querySelector('.labratgifts-fbt-loading');
    const empty = this.modal.querySelector('.labratgifts-fbt-empty');
    if (loading) loading.style.display = 'block';
    if (empty) empty.style.display = 'none';
    if (this.recommendationsContainer) this.recommendationsContainer.style.display = 'none';
  }

  hideLoading() {
    const loading = this.modal.querySelector('.labratgifts-fbt-loading');
    if (loading) loading.style.display = 'none';
  }

  showEmptyState() {
    this.hideLoading();
    const empty = this.modal.querySelector('.labratgifts-fbt-empty');
    if (empty) empty.style.display = 'flex';
    if (this.recommendationsContainer) this.recommendationsContainer.style.display = 'none';
  }

  formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    // Fallback to basic formatting
    return '$' + (cents / 100).toFixed(2);
  }
}

// Auto-initialize CartFBT when settings are ready
// Handles both early (snippet loads first) and late (script loads first) scenarios
function initCartFBT() {
  // Prevent multiple initializations - only set after successful construction
  if (window.labratgifts_fbt_initialized) {
    return;
  }
  
  // Check if settings are available and enabled
  if (window.labratgifts_fbt_settings && window.labratgifts_fbt_settings.enabled) {
    // Check if modal element exists
    const modalElement = document.getElementById('fbtModal');
    if (modalElement) {
      new CartFBT();
    } else {
      // Modal not ready yet - retry after a short delay
      console.log('FBT modal element not yet in DOM, retrying...');
      requestAnimationFrame(() => {
        setTimeout(initCartFBT, 50);
      });
    }
  }
}

// Primary: Check if settings are already ready (snippet loaded before script)
if (window.labratgifts_fbt_settings_ready) {
  // Settings already available, initialize immediately or on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartFBT);
  } else {
    initCartFBT();
  }
} else {
  // Settings not ready yet, listen for the event (script loaded before snippet)
  document.addEventListener('fbt:settings-ready', initCartFBT);
  
  // Also add DOMContentLoaded fallback
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartFBT);
  }
}
